import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { StyleSheet, View } from 'react-native';

import { IconOfferOutline, Text, tokens } from '@fhs/ui';
import useDialogModal from '@fhs-legacy/frontend/src/hooks/use-dialog-modal';

import { IDialogCallbacks } from '../types';

interface IRemoveIncentiveModalContext {
  show: (props: {
    offerName: string;
    heading?: string;
    description?: string;
    callbacks: IDialogCallbacks;
  }) => void;
  close: () => void;
}

interface IRemoveIncentiveModalState {
  offerName: string;
  heading?: string;
  description?: string;
  callbacks: IDialogCallbacks;
}

const initialModalState: IRemoveIncentiveModalState = {
  offerName: '',
  heading: undefined,
  description: undefined,
  callbacks: {},
};

const RemoveIncentiveModalContext = createContext<IRemoveIncentiveModalContext | null>(null);

export const useRemoveIncentiveModal = (): IRemoveIncentiveModalContext => {
  const context = useContext(RemoveIncentiveModalContext);
  if (!context) {
    throw new Error('useRemoveIncentiveModal must be used within a RemoveIncentiveModalProvider');
  }
  return context;
};
// Backwards-compatible alias for existing imports.
export const useRemoveIncentiveStore = useRemoveIncentiveModal;

interface IRemoveIncentiveModalProps {
  onDismiss: () => void;
  onConfirm: () => void;
  isOpen: boolean;
  offerName: string;
  description?: string;
  heading?: string;
}

const Modal = ({
  onDismiss,
  onConfirm,
  isOpen,
  offerName,
  heading = 'Remove Offer',
  description = 'Are you sure you want to remove this offer from your cart?',
}: IRemoveIncentiveModalProps) => {
  const [RemoveIncentiveDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Confirm Remove Incentive',
    showCancel: true,
    init: true,
    onDismiss: onDismiss,
    onConfirm: onConfirm,
    onCancel: onDismiss,
  });

  return (
    <RemoveIncentiveDialog
      showDialog={isOpen}
      ariaLabel={heading}
      headingComponent={<Text.Heading type="one">{heading}</Text.Heading>}
      bodyComponent={
        <View style={styles.content}>
          <Text.Ui size="md">{description}</Text.Ui>
          <View style={styles.nameContainer}>
            <View style={styles.iconContainer}>
              <IconOfferOutline size={24} />
            </View>
            <View style={{ flex: 1 }}>
              <Text.Heading type="four" numberOfLines={1}>
                {offerName}
              </Text.Heading>
            </View>
          </View>
        </View>
      }
      buttonLabel="Remove"
      showCloseButton
    />
  );
};

interface IRemoveIncentiveModalProviderProps {
  children: React.ReactNode;
}

export const RemoveIncentiveModalProvider = ({ children }: IRemoveIncentiveModalProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalState, setModalState] = useState<IRemoveIncentiveModalState>(initialModalState);

  const show = useCallback(
    ({
      offerName,
      heading,
      description,
      callbacks,
    }: {
      offerName: string;
      heading?: string;
      description?: string;
      callbacks: IDialogCallbacks;
    }) => {
      setModalState({
        offerName,
        heading,
        description,
        callbacks,
      });
      setIsOpen(true);
    },
    []
  );

  const close = useCallback(() => {
    setIsOpen(false);
    setModalState(initialModalState);
  }, []);

  const handleConfirm = useCallback(() => {
    modalState.callbacks.onConfirm?.();
    close();
  }, [close, modalState.callbacks]);

  const handleDismiss = useCallback(() => {
    modalState.callbacks.onDismiss?.();
    close();
  }, [close, modalState.callbacks]);

  const contextValue = useMemo(() => ({ show, close }), [show, close]);

  return (
    <RemoveIncentiveModalContext.Provider value={contextValue}>
      {children}
      {isOpen && (
        <Modal
          offerName={modalState.offerName}
          heading={modalState.heading}
          description={modalState.description}
          onConfirm={handleConfirm}
          onDismiss={handleDismiss}
          isOpen={isOpen}
        />
      )}
    </RemoveIncentiveModalContext.Provider>
  );
};

const styles = StyleSheet.create({
  content: {
    marginTop: 12,
    marginBottom: 20,
  },
  nameContainer: {
    flexDirection: 'row',
    gap: 12,
    borderWidth: 1,
    borderColor: tokens.colors.$blackOpacity10,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 16,
    marginTop: 20,
    justifyContent: 'center',
    alignItems: 'center',
  },
  iconContainer: {
    height: 24,
    width: 24,
  },
});
